import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ClipboardList, Bell, Activity, ArrowRight, Compass } from "lucide-react";
import AppShell from "@/components/AppShell";
import KpiCard from "@/components/KpiCard";
import AiChatDrawer from "@/components/ai/AiChatDrawer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function PatientDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/patient/dashboard");
      setData(data);
    })();
  }, []);

  const upcoming = data?.upcoming_appointments || [];

  return (
    <AppShell
      pageTitle={`Namaste, ${data?.user?.name || "there"}`}
      pageDescription="Your health, verified. Track appointments, records, and reminders in one calm space."
      actions={
        <div className="flex items-center gap-2">
          <AiChatDrawer
            endpoint="/ai/patient-chat"
            title="Health Co-pilot"
            description="Ask about your biomarkers, opportunities, or what to raise with your doctor. Grounded on your own data — never diagnostic."
            buttonLabel="Health Co-pilot"
            starterPrompts={[
              "What do my recent biomarkers mean?",
              "What should I ask my doctor at the next visit?",
              "Which of my opportunities should I tackle first?",
            ]}
            historyEndpoints={{
              list: "/ai/patient-chat/sessions",
              detail: (sid) => `/ai/patient-chat/history?session_id=${encodeURIComponent(sid)}`,
            }}
            testid="patient-ai"
          />
          <Link to="/patient/triage" data-testid="quick-triage-cta">
            <Button className="rounded-md">
              Start symptom triage <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Upcoming appointments" value={upcoming.length} icon={Calendar} testid="kpi-appointments" tone="mint" />
        <KpiCard label="Medical records" value={data?.records_count ?? 0} icon={ClipboardList} testid="kpi-records" tone="sky" />
        <KpiCard label="Active reminders" value={(data?.reminders || []).length} icon={Bell} testid="kpi-reminders" tone="lemon" />
        <KpiCard label="Care journey entries" value={(data?.care_journey || []).length} icon={Activity} testid="kpi-journey" tone="peach" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl text-primary">Upcoming appointments</h2>
            <Link to="/patient/appointments" className="text-sm text-primary hover:underline" data-testid="link-all-appointments">See all</Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No upcoming appointments.{" "}
              <Link to="/directory" className="text-primary font-medium hover:underline">Find a doctor →</Link>
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="upcoming-list">
              {upcoming.map((a) => (
                <li key={a.id} className="py-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-primary">{a.doctor_name}</div>
                    <div className="text-xs text-muted-foreground">{a.doctor_specialty} · {formatDate(a.date)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Reason: {a.reason}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider rounded-full border border-border bg-muted px-2 py-0.5">
                    {a.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-border bg-white p-6">
          <h2 className="font-serif text-2xl text-primary mb-4">Reminders</h2>
          <ul className="space-y-3" data-testid="reminders-list">
            {(data?.reminders || []).map((r) => (
              <li key={r.id} className="text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span className="font-medium text-primary">{r.title}</span>
                </div>
                <div className="text-xs text-muted-foreground ml-3.5 mt-0.5">
                  {formatDate(r.when)} · {r.type}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-white p-6">
          <h2 className="font-serif text-2xl text-primary mb-4">Care journey</h2>
          <ol className="relative border-l-2 border-border ml-2 space-y-4">
            {(data?.care_journey || []).map((c, i) => (
              <li key={i} className="pl-4">
                <span className="absolute -left-[7px] mt-1 w-3 h-3 rounded-full bg-primary" />
                <div className="text-sm font-medium text-primary">{c.title}</div>
                <div className="text-xs text-muted-foreground">{c.provider} · {formatDate(c.date)}</div>
              </li>
            ))}
          </ol>
        </div>
        <Link to="/directory" className="block rounded-lg border border-border bg-primary text-primary-foreground p-6 hover:opacity-95 transition-opacity" data-testid="patient-explore-directory">
          <Compass size={20} className="opacity-80" />
          <h3 className="mt-4 font-serif text-2xl">Find verified providers</h3>
          <p className="mt-2 text-sm text-primary-foreground/80">Search doctors and hospitals filtered by Trust Index tier.</p>
          <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium">Explore directory <ArrowRight size={14} /></span>
        </Link>
      </div>
    </AppShell>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
